import { getOptionalNumber } from '~/utils/valueParsers';

import type { RoomRateRoom } from '../RoomRatesStep/utils';
import { normaliseRoomName } from '../utils';

type SalesforceRoomAssetOccupancySource = {
  included_number_of_adults__c?: string | number | null;
  included_number_of_children__c?: string | number | null;
  included_number_of_infant__c?: string | number | null;
  included_number_of_teenagers__c?: string | number | null;
  maximum_occupancy_number__c?: string | number | null;
  name?: string | null;
};

type GetOccupancyPayloadByRoomTypeIdParams = {
  roomAssets: Array<SalesforceRoomAssetOccupancySource | null | undefined>;
  rooms: Array<RoomRateRoom>;
  selectedRoomTypeIds: Array<string>;
};

export type RoomRateOccupancyPayload = {
  include_infants: boolean;
  max_adult_occupancy: number;
  max_child_occupancy: number;
  max_infant_occupancy: number;
  max_occupancy: number;
  max_teenager_occupancy: number;
};

export function getRoomRateOccupancyPayloadByRoomTypeId({
  roomAssets,
  rooms,
  selectedRoomTypeIds,
}: GetOccupancyPayloadByRoomTypeIdParams) {
  const selectedRoomTypeIdSet = new Set(selectedRoomTypeIds);
  const occupancyByAssetName = new Map<string, RoomRateOccupancyPayload>();

  roomAssets.forEach((roomAsset) => {
    const roomName = normaliseRoomName(roomAsset?.name);

    if (!roomName || occupancyByAssetName.has(roomName)) {
      return;
    }

    const maxAdultOccupancy = getOptionalNumber(roomAsset?.included_number_of_adults__c);
    const maxChildOccupancy = getOptionalNumber(roomAsset?.included_number_of_children__c);
    const maxInfantOccupancy = getOptionalNumber(roomAsset?.included_number_of_infant__c);
    const maxOccupancy = getOptionalNumber(roomAsset?.maximum_occupancy_number__c);
    const maxTeenagerOccupancy = getOptionalNumber(roomAsset?.included_number_of_teenagers__c);

    if (
      maxAdultOccupancy === undefined ||
      maxChildOccupancy === undefined ||
      maxInfantOccupancy === undefined ||
      maxOccupancy === undefined ||
      maxTeenagerOccupancy === undefined
    ) {
      return;
    }

    occupancyByAssetName.set(roomName, {
      include_infants: true,
      max_adult_occupancy: maxAdultOccupancy,
      max_child_occupancy: maxChildOccupancy,
      max_infant_occupancy: maxInfantOccupancy,
      max_occupancy: maxOccupancy,
      max_teenager_occupancy: maxTeenagerOccupancy,
    });
  });

  return new Map(
    rooms
      .filter((room) => selectedRoomTypeIdSet.has(room.id))
      .flatMap((room) => {
        const occupancyPayload = occupancyByAssetName.get(normaliseRoomName(room.name));
        return occupancyPayload ? [[room.id, occupancyPayload] as [string, RoomRateOccupancyPayload]] : [];
      }),
  );
}
